import * as React from 'react';

import { hasSchoolOrDistrictPremium } from './userSubscription';

import useModalAccessibility from '../../../Shared/hooks/useModalAccessibility';

export const circleCheckSrc = `${window.postbuildEnv.CDN_URL}/images/pages/administrator/integrations/circle-check.svg`
export const circleCheckImg = <img alt="" src={circleCheckSrc} />

const TEACHER = 'teacher'

const TEACHER_NON_PREMIUM = 'teacherNonPremium'
const ADMIN_NON_PREMIUM = 'adminNonPremium'
const TEACHER_PREMIUM = 'teacherPremium'
const ADMIN_PREMIUM = 'adminPremium'

const PRIMARY_BUTTON_STYLE = "quill-button contained green medium focus-on-light"

// copy
const UNLOCK_CLASSLINK_WITH_SCHOOL_OR_DISTRICT_PREMIUM = 'Unlock ClassLink with School or District Premium'
const SEAMLESSLY_IMPORT_YOUR_CLASSLINK_ROSTERS = 'Seamlessly import your ClassLink rosters.'
const AUTOMATICALLY_CREATE_AND_SYNC_CLASSLINK_STUDENT_ACCOUNTS = 'Automatically create and sync ClassLink student accounts.'
const ACCESS_A_HOST_OF_OTHER_PREMIUM_BENEFITS = 'Access a host of other premium benefits, including priority technical support, enhanced reporting, and assistance from our professional learning team.'
const EXPLORE_PREMIUM = 'Explore premium'
const CONTACT_YOUR_ADMINISTRATOR = 'Contact your administrator'


// links
const RELATIVE_PREMIUM_HREF = '/premium'
const CLASSLINK_SUPPORT_GUIDE_HREF = 'https://support.quill.org/en/articles/11321582-how-do-i-set-up-a-classlink-rostering-integration-for-my-school-district-for-classlink-quill-administrators'

const TEACHER_NON_PREMIUM_EMAIL_HREF = `mailto:?subject=Can you help unlock Quill's ClassLink integration?&body=Hi [Insert Your Administrator’s Name],%0D%0A%0D%0A
I've been digging into the ClassLink integration in Quill, and it seems like a fantastic tool for our teaching needs.
To tap into its full potential, we'd need a School Premium or District Premium subscription.%0D%0A%0D%0A
For more details on Quill's premium offerings, they have a page listing the benefits and pricing here:
https://www.quill.org/premium.%0D%0A%0D%0A
If you're curious about how Quill’s ClassLink integration works, you can learn more here:
${CLASSLINK_SUPPORT_GUIDE_HREF}.%0D%0A%0D%0A
I'd appreciate it if you would consider reaching out to Quill at sales@quill.org to discuss the possibility of upgrading our
subscription. This could open up some exciting opportunities for us.%0D%0A%0D%0A
Thank you for your time and consideration.%0D%0A%0D%0ABest regards,%0D%0A%0D%0A[Insert Your Name]`


const classlinkStates = {
  [TEACHER_NON_PREMIUM]: {
    header: UNLOCK_CLASSLINK_WITH_SCHOOL_OR_DISTRICT_PREMIUM,
    paragraphCopy: 'Looking to streamline your teaching process with Quill’s ClassLink integration? Subscribe to School or District Premium to:',
    checklistItems: [
      SEAMLESSLY_IMPORT_YOUR_CLASSLINK_ROSTERS,
      AUTOMATICALLY_CREATE_AND_SYNC_CLASSLINK_STUDENT_ACCOUNTS,
      ACCESS_A_HOST_OF_OTHER_PREMIUM_BENEFITS
    ],
    buttons: [
      <a className={PRIMARY_BUTTON_STYLE} href={RELATIVE_PREMIUM_HREF} key={EXPLORE_PREMIUM} rel="noopener noreferrer" target="_blank">{EXPLORE_PREMIUM}</a>,
      <a className={PRIMARY_BUTTON_STYLE} href={TEACHER_NON_PREMIUM_EMAIL_HREF} key={CONTACT_YOUR_ADMINISTRATOR} rel="noopener noreferrer" target="_blank">{CONTACT_YOUR_ADMINISTRATOR}</a>,
    ]
  },

  [ADMIN_NON_PREMIUM]: {
    header: UNLOCK_CLASSLINK_WITH_SCHOOL_OR_DISTRICT_PREMIUM,
    paragraphCopy: 'Looking to streamline your teaching process with Quill’s ClassLink integration? Subscribe to School or District Premium to:',
    checklistItems: [
      SEAMLESSLY_IMPORT_YOUR_CLASSLINK_ROSTERS,
      AUTOMATICALLY_CREATE_AND_SYNC_CLASSLINK_STUDENT_ACCOUNTS,
      ACCESS_A_HOST_OF_OTHER_PREMIUM_BENEFITS
    ],
    buttons: [
      <a className={PRIMARY_BUTTON_STYLE} href={RELATIVE_PREMIUM_HREF} key={EXPLORE_PREMIUM} rel="noopener noreferrer" target="_blank">{EXPLORE_PREMIUM}</a>,
    ]
  },

  [TEACHER_PREMIUM]: {
    header: 'Contact your administrator to unlock ClassLink',
    paragraphCopy: `Looking to streamline your teaching process with Quill’s ClassLink integration?
      Great news! Your Quill Premium subscription gives you access to the integration.
      The next step is for your administrator to set it up. Once they have done so, you'll be able to:`,
    checklistItems: [
      SEAMLESSLY_IMPORT_YOUR_CLASSLINK_ROSTERS,
      AUTOMATICALLY_CREATE_AND_SYNC_CLASSLINK_STUDENT_ACCOUNTS
    ],
    buttons: [
      <a className={PRIMARY_BUTTON_STYLE} href={TEACHER_NON_PREMIUM_EMAIL_HREF} key={CONTACT_YOUR_ADMINISTRATOR} rel="noopener noreferrer" target="_blank">{CONTACT_YOUR_ADMINISTRATOR}</a>
    ]
  },

  [ADMIN_PREMIUM]: {
    header: 'Get started with ClassLink',
    paragraphCopy: `Looking to streamline your school's teaching process with Quill’s ClassLink integration?
      Great news! Your Quill Premium subscription gives you access to the integration.
      The next step is to set it up. This will allow teachers at your school to:`,
    checklistItems: [
      SEAMLESSLY_IMPORT_YOUR_CLASSLINK_ROSTERS,
      AUTOMATICALLY_CREATE_AND_SYNC_CLASSLINK_STUDENT_ACCOUNTS
    ],
    buttons: [
      <a className={PRIMARY_BUTTON_STYLE} href={CLASSLINK_SUPPORT_GUIDE_HREF} key='admin_premium' rel="noopener noreferrer" target="_blank">Set up integration</a>
    ]
  },
}

const computeClasslinkState = (user) => {
  const isPremium = hasSchoolOrDistrictPremium(user)
  if (user.role === TEACHER) {
    return isPremium ? TEACHER_PREMIUM : TEACHER_NON_PREMIUM;
  } else {
    return isPremium ? ADMIN_PREMIUM : ADMIN_NON_PREMIUM;
  }
}

const ClasslinkModal = ({ close, user }) => {
  const { modalRef } = useModalAccessibility(close);

  const classlinkState = computeClasslinkState(user);

  return (
    <div className="modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="ClasslinkModalTitle"
        aria-modal="true"
        className="classlink-modal quill-modal modal-body"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >
        <div>
          <h3 className="title">{classlinkStates[classlinkState].header}</h3>
        </div>
        <p>{classlinkStates[classlinkState].paragraphCopy}</p>
        <ul>
          {classlinkStates[classlinkState].checklistItems.map(checklistItem => (
            <li key={checklistItem}>{circleCheckImg}{checklistItem}</li>
          ))}
        </ul>
        <div className="form-buttons">
          <button
            className="quill-button outlined green medium focus-on-light"
            onClick={close}
            type="button"
          >
            Cancel
          </button>
          <div className="right-aligned-buttons">
            {classlinkStates[classlinkState].buttons}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClasslinkModal;
